import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './Dashboard.css';
import Footer from './Footer';
import examService from '../services/examService';
import examTypeService from '../services/examTypeService';
import progressReportService from '../services/progressReportService';
import examSessionStorage from '../services/examSessionStorage';
import apiInterceptor from '../services/apiInterceptor';
import { useAuth } from '../contexts/AuthContext';
import {
  PieChart, Pie, Cell, Tooltip as RechartTooltip, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer,
  AreaChart, Area, RadialBarChart, RadialBar
} from 'recharts';
import { useTheme } from '../contexts/ThemeContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import NotificationBell from './NotificationBell';
import { 
  faChartLine, 
  faInfoCircle,
  faCalendarDay,
  faPlayCircle,
  faTrashAlt,
  faClock,
  faQuestionCircle,
  faTimes,
  faEye,
  faSortAmountUp,
  faSortAmountDown
} from '@fortawesome/free-solid-svg-icons';

const pieData = [
  { name: 'Data will be populated after some activity', value: 0 },
  // { name: 'Neurology', value: 0 },
  // { name: 'Pharmacology', value: 0 },
  // { name: 'Microbiology', value: 0 },
  // { name: 'Biochemistry', value: 0 },
];

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#9b59b6'];

const dailyData = [
  { day: 'Mon', tests: 0 },
  { day: 'Tue', tests: 0 },
  { day: 'Wed', tests: 0 },
  { day: 'Thu', tests: 0 },
  { day: 'Fri', tests: 0 },
  { day: 'Sat', tests: 0 },
  { day: 'Sun', tests: 0 },
];

// Mock data - will be replaced with real data from API
const mockStudyTimeData = [
  { day: 'Mon', hours: 0 },
  { day: 'Tue', hours: 0 },
  { day: 'Wed', hours: 0 },
  { day: 'Thu', hours: 0 },
  { day: 'Fri', hours: 0 },
  { day: 'Sat', hours: 0 },
  { day: 'Sun', hours: 0 },
];

// Adding new mock data for Strong vs Weak category chart
const categoryPerformanceData = [
  { category: 'Cardiology', correct: 0, total: 0 },
  { category: 'Neurology', correct: 0, total: 0 },
  { category: 'Pharmacology', correct: 0, total: 0 },
  { category: 'Microbiology', correct: 0, total: 0 },
  { category: 'Biochemistry', correct: 0, total: 0 },
];

// Mock data for total questions progress
const questionsProgressData = [
  { name: 'Total Questions', value: 0, fill: '#8884d8' },
  { name: 'Completed', value: 0, fill: '#82ca9d' },
  { name: 'Correct', value: 0, fill: '#0088FE' },
];

function Dashboard() {
  const [examType, setExamType] = useState('CBSE');
  const [examTypes, setExamTypes] = useState([]);
  const [selectedExamTypeId, setSelectedExamTypeId] = useState(null);
  const { isDarkMode } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Chart data states
  const [categoryData, setCategoryData] = useState([]);
  const [rawCategoryReport, setRawCategoryReport] = useState([]);
  const [dailyPerformanceData, setDailyPerformanceData] = useState([]);
  const [studyTimeData, setStudyTimeData] = useState(mockStudyTimeData);
  const [accuracyReportData, setAccuracyReportData] = useState([]);
  const [accuracySortOrder, setAccuracySortOrder] = useState('high-to-low');
  const [chartLoading, setChartLoading] = useState(true);
  const [chartError, setChartError] = useState(null);
  
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: null,
    type: 'delete' // Only 'delete' type now
  });
  // Removed calculateProgress function - no longer needed since we get progress data directly from exam endpoint

  // Utility function to format time in seconds to human readable format
  const formatTime = (seconds) => {
    if (seconds < 60) {
      return `${seconds}s`;
    } else if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      return `${minutes}m`;
    } else {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      return `${hours}h ${minutes}m`;
    }
  };  // Function to process accuracy report data
  const processAccuracyReportData = useCallback((categoryReport) => {
    try {
      if (!categoryReport || categoryReport.length === 0) {
        setAccuracyReportData([]);
        return;
      }

      // Filter out categories with no activity and process data
      const processedData = categoryReport
        .filter(cat => cat.questionsAnswered > 0) // Only show categories with activity
        .map(cat => ({
          categoryName: cat.categoryName,
          accuracyPercentage: Math.round(cat.accuracyPercentage || 0),
          questionsAnswered: cat.questionsAnswered,
          totalQuestions: cat.totalQuestions,
          completionPercentage: Math.round(cat.completionPercentage || 0)
        }));

      // Sort the data based on current sort order
      const sortedData = [...processedData].sort((a, b) => {
        if (accuracySortOrder === 'high-to-low') {
          return b.accuracyPercentage - a.accuracyPercentage;
        } else {
          return a.accuracyPercentage - b.accuracyPercentage;
        }
      });

      //console.log('Processed accuracy report data:', sortedData);
      setAccuracyReportData(sortedData);

    } catch (error) {
      console.error('Error processing accuracy report data:', error);
      setAccuracyReportData([]);
    }
  }, [accuracySortOrder]);

  // Function to toggle sort order
  const toggleAccuracySortOrder = useCallback(() => {
    const newSortOrder = accuracySortOrder === 'high-to-low' ? 'low-to-high' : 'high-to-low';
    setAccuracySortOrder(newSortOrder);
  }, [accuracySortOrder]);

  // Function to process exam sessions and create study time data
  const processStudyTimeData = useCallback((examSessions) => {
    try {
      // Get the last 7 days
      const today = new Date();
      const last7Days = [];
      
      for (let i = 6; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        last7Days.push({
          date: date.toISOString().split('T')[0], // YYYY-MM-DD format
          day: date.toLocaleDateString('en-US', { weekday: 'short' }), // Mon, Tue, etc.
          hours: 0
        });
      }

      // Process exam sessions to calculate daily study time
      if (examSessions && examSessions.length > 0) {
        examSessions.forEach(exam => {
          try {
            if (exam.startDate && exam.timeSpent && exam.timeSpent > 0) {
              const examDate = new Date(exam.startDate).toISOString().split('T')[0];
              const dayData = last7Days.find(day => day.date === examDate);
              
              if (dayData) {
                // Convert timeSpent from minutes to hours
                const hoursSpent = Math.max(0, exam.timeSpent / 60); // Ensure non-negative
                dayData.hours += hoursSpent;
              }
            }
          } catch (examError) {
            console.warn('Error processing individual exam session:', examError, exam);
          }
        });
      }

      // Round hours to 1 decimal place and ensure reasonable values
      const processedData = last7Days.map(day => ({
        day: day.day,
        hours: Math.min(24, Math.round(day.hours * 10) / 10), // Cap at 24 hours per day
        date: day.date
      }));

      //console.log('Processed study time data:', processedData);
      setStudyTimeData(processedData);
      
    } catch (error) {
      //console.error('Error processing study time data:', error);
      // Fallback to mock data on error
      setStudyTimeData(mockStudyTimeData);
    }
  }, []);

  // Fetch study sessions function - extracted for reuse
  const fetchStudySessions = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);          // Get exam sessions from the API (both started and finished sessions)
      const params = {
        limit: 20 // Increase limit to get more data for study time analysis
      };
      
      // Add ExamTypeId filter if a specific exam type is selected
      if (selectedExamTypeId) {
        params.ExamTypeId = selectedExamTypeId;
      }
      
      const examSessions = await examService.getExamSessions(params);

      if (!examSessions || examSessions.length === 0) {
        setSessions([]);
        // Set empty study time data when no sessions
        setStudyTimeData(mockStudyTimeData);
        return;
      }

      // Process study time data from all exam sessions
      processStudyTimeData(examSessions);// Process sessions using exam data directly - no need to fetch responses anymore
      const processedSessions = examSessions.slice(0, 20).map((exam) => {
        // Use questionCount and responseCount directly from exam API response
        const totalQuestions = exam.questionCount || 0;
        const completedQuestions = exam.responseCount || 0;
        const progressPercent = totalQuestions > 0 ? Math.round((completedQuestions / totalQuestions) * 100) : 0;
        
        return {
          id: exam.id,
          title: exam.title ? `${exam.title} - ${exam.type} Session` : `${exam.type} Session`,
          totalQuestions: totalQuestions,
          completedQuestions: completedQuestions,
          // Only include timeSpent for Practice exams, use timeSpent from exam API response
          timeSpent: exam.type?.toLowerCase() === 'practice' ? formatTime(exam.timeSpent || 0) : null,
          lastAccessed: exam.endDate ? new Date(exam.endDate).toLocaleDateString() : 
                       exam.startDate ? new Date(exam.startDate).toLocaleDateString() : 'Today',
          status: exam.endDate ? 'completed' : (exam.status?.toLowerCase() === 'started' ? 'in-progress' : exam.status),
          progressPercent: progressPercent,
          examData: exam, // Store original exam data for resume functionality
          examType: exam.type // Store exam type for conditional rendering
        };
      });

      setSessions(processedSessions);
    } catch (err) {
     // console.error('Failed to fetch study sessions:', err);
      setError('Failed to load study sessions. Please try again later.');
      // Fallback to empty array rather than showing mock data
      setSessions([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedExamTypeId, processStudyTimeData]);

  // Fetch study sessions data
  // Updated to use questionCount and responseCount directly from exam API response
  // No longer fetches responses for progress calculation
  useEffect(() => {
    fetchStudySessions();
  }, [fetchStudySessions]);

  // Clear active practice test session when navigating to dashboard
  useEffect(() => {
    //console.log('[Dashboard] Clearing active practice test session on dashboard load');
    // examSessionStorage.clearExamData(); // Commented out to allow resume functionality
  }, []);

    // Fetch exam types from API
  useEffect(() => {
    const fetchExamTypes = async () => {
      try {
        const types = await examTypeService.getExamTypes();
        setExamTypes(types);
        
        // Set default exam type to first one or find CBSE
        if (types && types.length > 0) {
          const cbseType = types.find(type => type.name === 'CBSE');
          if (cbseType) {
            setSelectedExamTypeId(cbseType.id);
            setExamType(cbseType.name);
          } else {
            setSelectedExamTypeId(types[0].id);
            setExamType(types[0].name);
          }
        }
      } catch (error) {
        console.error('Failed to fetch exam types:', error);
        // Fallback to hardcoded CBSE if API fails
        setExamTypes([{ id: null, name: 'CBSE' }]);
        setExamType('CBSE');
      }
    };    fetchExamTypes();
  }, []);

  // Fetch chart data from progress report API
  useEffect(() => {
    const fetchChartData = async () => {
      try {
        setChartLoading(true);
        setChartError(null);

        // Fetch category data and daily performance data in parallel
        const [categoryReport, dailyReport] = await Promise.all([
          progressReportService.getTestByCategoryReport({
            ExamTypeId: selectedExamTypeId
          }),
          progressReportService.getDailyPerformanceReport(7, selectedExamTypeId)
        ]);        // Process category data for pie chart
        if (categoryReport && categoryReport.length > 0) {
          // Store raw category report data for accuracy chart
          setRawCategoryReport(categoryReport);
          
          // Process accuracy report data
          processAccuracyReportData(categoryReport);
          
          // Get sorted categories by completion percentage
          const sortedCategories = categoryReport
            .filter(cat => cat.questionsAnswered > 0) // Only show categories with activity
            .sort((a, b) => b.completionPercentage - a.completionPercentage);
          
          const top5 = sortedCategories.slice(0, 5);
          const others = sortedCategories.slice(5);
          
          const processedCategoryData = top5.map(cat => ({
            name: cat.categoryName,
            value: cat.questionsAnswered,
            total: cat.totalQuestions,
            accuracy: cat.accuracyPercentage,
            completion: cat.completionPercentage
          }));

          // Add "Others" if there are more than 5 categories
          if (others.length > 0) {
            const othersTotal = others.reduce((sum, cat) => sum + cat.questionsAnswered, 0);
            processedCategoryData.push({
              name: 'Others',
              value: othersTotal,
              total: others.reduce((sum, cat) => sum + cat.totalQuestions, 0),
              accuracy: others.length > 0 ? Math.round(others.reduce((sum, cat) => sum + cat.accuracyPercentage, 0) / others.length) : 0,
              completion: others.length > 0 ? Math.round(others.reduce((sum, cat) => sum + cat.completionPercentage, 0) / others.length) : 0
            });
          }

          setCategoryData(processedCategoryData);
        } else {
          setCategoryData([]);
          setRawCategoryReport([]);
        }

        // Set daily performance data
        setDailyPerformanceData(dailyReport || []);

      } catch (err) {
        console.error('Failed to fetch chart data:', err);
        setChartError('Failed to load chart data. Using sample data.');
          // Fallback to empty data instead of mock data for a cleaner experience
        setCategoryData([]);
        setRawCategoryReport([]);
        setDailyPerformanceData([]);
      } finally {
        setChartLoading(false);
      }
    };

    // Only fetch chart data if we have exam types loaded
    if (examTypes.length > 0) {
      fetchChartData();
    }
  }, [selectedExamTypeId, examTypes, processAccuracyReportData]);

  // Re-process accuracy data when sort order changes
  useEffect(() => {
    if (rawCategoryReport && rawCategoryReport.length > 0) {
      processAccuracyReportData(rawCategoryReport);
    }
  }, [accuracySortOrder, rawCategoryReport, processAccuracyReportData]);

  // Handle exam type selection change
  const handleExamTypeChange = (selectedTypeName) => {
    const selectedType = examTypes.find(type => type.name === selectedTypeName);
    if (selectedType) {
      setSelectedExamTypeId(selectedType.id);
      setExamType(selectedType.name);
    } else {
      // Fallback if type not found
      setSelectedExamTypeId(null);
      setExamType(selectedTypeName);
    }
  };
    
    // Get the current date dynamically
  const currentDate = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // Handler functions for study session actions
  const handleResumeSession = (sessionId) => {
    const session = sessions.find(s => s.id === sessionId);
    if (session?.examData?.id) {
      // Navigate to practice test with the exam ID to resume the session
      navigate(`/practice-test/${session.examData.id}?resume=true`);
    } else {
      console.error('Cannot resume session: missing exam data');
    }
  };  const handleDeleteSession = (sessionId) => {
    setConfirmDialog({
      open: true,
      title: "Delete Study Session",
      message: "Are you sure you want to delete this study session? This action cannot be undone.",
      type: 'delete',
      onConfirm: async () => {
        try {
          // Get the session to find the exam ID using current sessions state
          const currentSessions = sessions;
          const session = currentSessions.find(s => s.id === sessionId);
          if (!session?.examData?.id) {
            console.error('Cannot delete session: missing exam ID');
            setConfirmDialog(prev => ({ ...prev, open: false }));
            return;
          }

          // Call the delete API
          const response = await examService.deleteExam(session.examData.id);
          
          // Check if deletion was successful
          if (response.success || response.ok || response.status === 204) {
            // console.log('Exam deleted successfully');
            // Refresh the sessions list to fetch new data and maintain "last 10" display
            await fetchStudySessions();
          } else if (response.status === 404) {
            console.error('Exam not found');
            // Still refresh since the exam doesn't exist on server
            await fetchStudySessions();
          } else {
            console.error('Failed to delete exam:', response.status || response.message);
            // Still remove from local state to maintain UI consistency
            setSessions(prevSessions => prevSessions.filter(s => s.id !== sessionId));
          }
        } catch (error) {
          console.error('Error deleting exam:', error);
          // On error, still remove from local state to maintain UI consistency
          setSessions(prevSessions => prevSessions.filter(s => s.id !== sessionId));
        } finally {
          setConfirmDialog(prev => ({ ...prev, open: false }));
        }
      }
    });
  };
  const handleCloseDialog = () => {
    setConfirmDialog(prev => ({ ...prev, open: false }));
  };

  // Theme-specific styles
  const themeStyles = useMemo(() => {
    return {
      dashboard: {
        background: isDarkMode
          ? 'linear-gradient(180deg, #0e1626 0%, #0b1220 100%)'
          : '#eceff4'
      },
      text: {
        primary: isDarkMode ? '#eaf0f7' : '#000000', // Change light mode text color to black
        secondary: isDarkMode ? '#9fb0c5' : 'rgba(0, 0, 0, 0.7)' // Adjust secondary text color for light mode
      },
      banner: {
        background: isDarkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.05)',
        border: isDarkMode ? '1px solid rgba(148, 163, 184, 0.12)' : '1px solid rgba(0, 0, 0, 0.05)'
      },
      select: {
        background: isDarkMode ? '#1c2740' : 'rgba(0, 0, 0, 0.1)',
        color: isDarkMode ? '#eaf0f7' : '#2c3e50'
      },
      chartBox: {
        background: isDarkMode ? '#151e30' : '#ffffff',
        shadow: isDarkMode ? '0 1px 2px rgba(0, 0, 0, 0.4), 0 8px 24px rgba(0, 0, 0, 0.28)' : '0 4px 10px rgba(0, 0, 0, 0.1)'
      },
      testItem: {
        background: isDarkMode ? '#1c2740' : 'rgba(0, 0, 0, 0.05)'
      },
      actionButton: {
        background: isDarkMode ? '#1c2740' : 'rgba(44, 62, 80, 0.1)',
        color: isDarkMode ? '#eaf0f7' : '#2c3e50'
      },
      studySessionItem: {
        background: isDarkMode ? '#1c2740' : 'rgba(0, 0, 0, 0.05)',
        border: isDarkMode ? '1px solid rgba(148, 163, 184, 0.12)' : '1px solid rgba(0, 0, 0, 0.05)',
        hover: isDarkMode ? '#243150' : 'rgba(0, 0, 0, 0.1)'
      },
      progressBar: {
        background: isDarkMode ? 'rgba(148, 163, 184, 0.16)' : 'rgba(0, 0, 0, 0.1)',
        fill: isDarkMode ? '#3d8bff' : '#4CAF50'
      },
      modalOverlay: {
        background: isDarkMode ? 'rgba(8, 12, 22, 0.72)' : 'rgba(0, 0, 0, 0.5)'
      },
      modal: {
        background: isDarkMode ? '#151e30' : '#ffffff',
        border: isDarkMode ? '1px solid rgba(148, 163, 184, 0.16)' : '1px solid rgba(0, 0, 0, 0.1)'
      },
      button: {
        confirm: isDarkMode ? '#f87171' : '#e74c3c',
        cancel: isDarkMode ? '#1c2740' : 'rgba(0, 0, 0, 0.1)',
        cancelText: isDarkMode ? '#eaf0f7' : '#333333',
        confirmText: '#ffffff'
      },
      gridLines: isDarkMode ? 'rgba(148, 163, 184, 0.12)' : 'rgba(0, 0, 0, 0.1)'
    };
  }, [isDarkMode]);

  const dropdownClass = isDarkMode ? 'dark-select' : 'light-select';

  // Computed chart data that uses API data when available, otherwise falls back to mock data
  const pieChartData = useMemo(() => {
    if (categoryData.length > 0) {
      return categoryData;
    }
    // Fallback to mock data
    return pieData;
  }, [categoryData]);

  const dailyChartData = useMemo(() => {
    if (dailyPerformanceData.length > 0) {
      return dailyPerformanceData.map(day => ({
        day: day.day,
        totalQuestions: day.totalQuestions,
        questionsAnswered: day.questionsAnswered,
        completionPercentage: day.completionPercentage
      }));
    }
    // Fallback to mock data
    return dailyData.map(day => ({ ...day, totalQuestions: day.tests * 5, questionsAnswered: day.tests * 3, completionPercentage: 60 }));
  }, [dailyPerformanceData]);  // State for showing all categories in the accuracy chart
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [selectedPieSegment, setSelectedPieSegment] = useState(null);
  
  // Handle window resize for responsive chart adjustments
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle pie chart segment click
  const handlePieClick = useCallback((data, index) => {
    if (windowWidth <= 768) {
      setSelectedPieSegment(selectedPieSegment?.index === index ? null : { ...data, index });
    }
  }, [windowWidth, selectedPieSegment]);

  // Computed category performance data for accuracy chart
  const accuracyChartData = useMemo(() => {
    if (rawCategoryReport.length > 0) {
      // Get all categories by accuracy for the accuracy chart (no "Others" group)
      const sortedByAccuracy = rawCategoryReport
        .filter(cat => cat.questionsAnswered > 0) // Only show categories with activity
        .sort((a, b) => b.accuracyPercentage - a.accuracyPercentage);
      
      return sortedByAccuracy.map(cat => ({
        category: cat.categoryName,
        correct: cat.correctAnswers,
        total: cat.questionsAnswered,
        accuracy: cat.accuracyPercentage
      }));
    }
    // Fallback to mock data
    return categoryPerformanceData;
  }, [rawCategoryReport]);

  // Chart data for display (top 5 or all based on state)
  const displayedAccuracyData = useMemo(() => {
    return showAllCategories ? accuracyChartData : accuracyChartData.slice(0, 5);
  }, [accuracyChartData, showAllCategories]);
  // Computed overall progress data for radial chart
  const overallProgressData = useMemo(() => {
    if (rawCategoryReport.length > 0) {
      const totalQuestions = rawCategoryReport.reduce((sum, cat) => sum + (cat.totalQuestions || 0), 0);
      const questionsAnswered = rawCategoryReport.reduce((sum, cat) => sum + (cat.questionsAnswered || 0), 0);
      const correctAnswers = rawCategoryReport.reduce((sum, cat) => sum + (cat.correctAnswers || 0), 0);
      
      return [
        { name: 'Total Questions', value: totalQuestions, fill: '#8884d8' },
        { name: 'Answered', value: questionsAnswered, fill: '#82ca9d' },
        { name: 'Correct', value: correctAnswers, fill: '#0088FE' },
      ];
    }
    // Fallback to mock data
    return questionsProgressData;
  }, [rawCategoryReport]);

  return (
    <div className="dashboard-container-main" style={themeStyles.dashboard}>
      {/* Welcome Banner */}
      <div className="welcome-banner" style={{
        background: themeStyles.banner.background,
        borderBottom: themeStyles.banner.border
      }}>        <div className="welcome-info">
          <h2 style={{ color: themeStyles.text.primary }}>
            Welcome, {user?.title || 'Student'}{user?.firstName && user?.lastName ? ` ${user.firstName} ${user.lastName}` : ''}
          </h2>
          <p className="welcome-subtext" style={{ color: themeStyles.text.secondary }}>Here's your performance overview</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <NotificationBell />
          <div className="current-date">
            <FontAwesomeIcon icon={faCalendarDay} className="calendar-icon" style={{ color: themeStyles.text.secondary }} />
            <span style={{ color: themeStyles.text.primary, paddingRight: '40px' }}>{currentDate}</span>
          </div>
        </div>
      </div>

      <div className="dashboard-header">
        <h1 style={{ color: themeStyles.text.primary }}>Dashboard</h1>        <div className="exam-type-selector">
          <span style={{ color: themeStyles.text.primary }}>Exam Type:</span>
          <select 
            id="exam-type" 
            value={examType} 
            onChange={(e) => handleExamTypeChange(e.target.value)}
            className={dropdownClass}
          >
            {examTypes.length === 0 ? (
              <option value="CBSE">CBSE</option>
            ) : (
              examTypes.map(type => (
                <option key={type.id} value={type.name}>
                  {type.name}
                </option>
              ))
            )}
          </select>
        </div>
      </div>

      <div className="dashboard-content">
        {/* Study Sessions Section */}
        <div className="chart-box study-sessions-box" style={{ 
          background: themeStyles.chartBox.background, 
          boxShadow: themeStyles.chartBox.shadow,
          marginBottom: '20px'
        }}>
          <div className="chart-header">
            <h3 style={{ color: themeStyles.text.primary }}>Recent Study Sessions</h3>
            <FontAwesomeIcon 
              icon={faInfoCircle} 
              className="info-icon" 
              title="Your active and recent study sessions" 
              style={{ color: themeStyles.text.secondary }}
            />
          </div>          <div className="study-sessions-list" style={{ 
            maxHeight: '300px', 
            overflowY: 'auto', 
            padding: '0 5px'
          }}>
            {isLoading ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '40px',
                color: themeStyles.text.secondary 
              }}>
                <div style={{ marginBottom: '10px' }}>Loading study sessions...</div>
                <div style={{
                  width: '40px',
                  height: '40px',
                  border: `3px solid ${themeStyles.text.secondary}`,
                  borderTop: '3px solid transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  margin: '0 auto'
                }}></div>
              </div>
            ) : error ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '40px',
                color: themeStyles.text.secondary 
              }}>
                <div style={{ marginBottom: '10px', color: '#f44336' }}>{error}</div>
                <button 
                  onClick={() => window.location.reload()}
                  style={{
                    padding: '8px 16px',
                    background: themeStyles.button?.primary?.background || '#2196F3',
                    color: themeStyles.button?.primary?.color || '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Retry
                </button>
              </div>
            ) : sessions.length === 0 ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '40px',
                color: themeStyles.text.secondary 
              }}>
                <p style={{ marginBottom: '20px' }}>No active study sessions found.</p>
                <button 
                  onClick={() => navigate('/practice-test')}
                  style={{
                    background: isDarkMode ? '#4DB6AC' : '#4CAF50',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '12px 24px',
                    fontSize: '16px',
                    cursor: 'pointer',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                  onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                >
                  Start with a Practice Test
                </button>
              </div>
            ) : (
              sessions.map(session => (
                <div 
                  key={session.id} 
                  className="study-session-item" 
                  style={{ 
                    background: themeStyles.studySessionItem.background,
                    border: themeStyles.studySessionItem.border,
                    borderRadius: '8px',
                    padding: '15px',
                    marginBottom: '10px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h4 style={{ color: themeStyles.text.primary, margin: '0' }}>{session.title}</h4>
                    <div style={{ display: 'flex', gap: '10px' }}>                      <button 
                        className="session-action-clean-button"
                        onClick={() => handleResumeSession(session.id)} 
                        title={session.examData?.endDate ? "Review Session" : "Resume Session"}
                        style={{ 
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          color: isDarkMode ? '#4DB6AC' : '#2E7D32',
                          padding: '4px',
                          transition: 'color 0.2s ease',
                          outline: 'none',
                          borderRadius: '4px',
                          boxShadow: 'none',
                          width: 'auto',
                          height: 'auto',
                          transform: 'none'
                        }}
                        onMouseEnter={(e) => {
                          if (!isDarkMode) {
                            e.target.style.color = '#1B5E20';
                            e.target.style.background = 'transparent';
                            e.target.style.border = 'none';
                            e.target.style.boxShadow = 'none';
                            e.target.style.transform = 'none';
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.color = isDarkMode ? '#4DB6AC' : '#2E7D32';
                          e.target.style.background = 'transparent';
                          e.target.style.border = 'none';
                          e.target.style.boxShadow = 'none';
                          e.target.style.transform = 'none';
                        }}
                      >
                        <FontAwesomeIcon icon={session.examData?.endDate ? faEye : faPlayCircle} size="lg" />
                      </button>
                      <button 
                        className="session-action-clean-button"
                        onClick={() => handleDeleteSession(session.id)} 
                        title="Delete Session"
                        style={{ 
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          color: isDarkMode ? '#EF5350' : '#C62828',
                          padding: '4px',
                          transition: 'color 0.2s ease',
                          outline: 'none',
                          borderRadius: '4px',
                          boxShadow: 'none',
                          width: 'auto',
                          height: 'auto',
                          transform: 'none'
                        }}
                        onMouseEnter={(e) => {
                          if (!isDarkMode) {
                            e.target.style.color = '#B71C1C';
                            e.target.style.background = 'transparent';
                            e.target.style.border = 'none';
                            e.target.style.boxShadow = 'none';
                            e.target.style.transform = 'none';
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.color = isDarkMode ? '#EF5350' : '#C62828';
                          e.target.style.background = 'transparent';
                          e.target.style.border = 'none';
                          e.target.style.boxShadow = 'none';
                          e.target.style.transform = 'none';
                        }}
                      >
                        <FontAwesomeIcon icon={faTrashAlt} size="lg" />
                      </button>
                    </div>
                  </div>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: themeStyles.text.secondary }}>
                    <FontAwesomeIcon icon={faQuestionCircle} size="sm" />
                    <span>{session.completedQuestions}/{session.totalQuestions} questions completed</span>
                  </div>
                  
                  <div style={{ width: '100%', height: '8px', background: themeStyles.progressBar.background, borderRadius: '4px', overflow: 'hidden' }}>
                    <div style={{ 
                      width: `${session.totalQuestions > 0 ? (session.completedQuestions / session.totalQuestions) * 100 : 0}%`, 
                      height: '100%', 
                      background: themeStyles.progressBar.fill,
                      borderRadius: '4px'
                    }}></div>
                  </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '5px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                      {/* Only show Time spent for Practice exams */}
                      {session.timeSpent && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: themeStyles.text.secondary }}>
                          <FontAwesomeIcon icon={faClock} size="sm" />
                          <span>Time spent: {session.timeSpent}</span>
                        </div>
                      )}
                    </div>
                    <span style={{ color: themeStyles.text.secondary, fontSize: '0.85rem' }}>
                      Last accessed: {session.lastAccessed}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {sessions.length > 0 && (
          <>
        {/* First row of charts */}
        <div className="charts-row">
          <div className="chart-box" style={{ 
            background: themeStyles.chartBox.background, 
            boxShadow: themeStyles.chartBox.shadow 
          }}>            <div className="chart-header">
              <h3 style={{ color: themeStyles.text.primary }}>Category Progress</h3>
              <FontAwesomeIcon 
                icon={faInfoCircle} 
                className="info-icon" 
                title="Questions answered by category" 
                style={{ color: themeStyles.text.secondary }}
              />
            </div>
            {chartLoading ? (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '250px',
                color: themeStyles.text.secondary 
              }}>
                Loading chart data...
              </div>
            ) : (
              <div>
                <ResponsiveContainer width="100%" height={windowWidth <= 480 ? 200 : windowWidth <= 768 ? 220 : 250}>
                  <PieChart>
                    <Pie 
                      data={pieChartData} 
                      dataKey="value" 
                      nameKey="name" 
                      cx="50%" 
                      cy="50%" 
                      outerRadius={windowWidth <= 768 ? 60 : windowWidth <= 480 ? 50 : 80} 
                      innerRadius={windowWidth <= 768 ? 35 : windowWidth <= 480 ? 25 : 50}
                      fill="#8884d8"
                      paddingAngle={2}
                      label={windowWidth <= 768 ? false : ({ name, percent }) => 
                        isDarkMode ? 
                          `${name}: ${(percent * 100).toFixed(0)}%` : 
                          `${name}`
                      }
                      labelLine={windowWidth <= 768 ? false : !isDarkMode}
                      onClick={handlePieClick}
                      style={{ cursor: windowWidth <= 768 ? 'pointer' : 'default' }}
                    >
                      {pieChartData.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={COLORS[index % COLORS.length]}
                          stroke={selectedPieSegment?.index === index ? '#fff' : 'none'}
                          strokeWidth={selectedPieSegment?.index === index ? 3 : 0}
                        />
                      ))}
                    </Pie>
                    <RechartTooltip 
                      cursor={
                        isDarkMode
                          ? { fill: 'rgba(0,0,0,0.25)', stroke: 'rgba(255,255,255,0.2)' }
                          : { fill: 'rgba(0,0,0,0.06)', stroke: 'rgba(0,0,0,0.2)' }
                      }
                      labelStyle={{ color: isDarkMode ? '#ffffff' : '#333' }}
                      itemStyle={{ color: isDarkMode ? '#ffffff' : '#333' }}
                      formatter={(value, name, props) => {
                        const textColor = isDarkMode ? '#ffffff' : '#333';
                        const data = props.payload;
                        if (data.total) {
                          return [
                            <span style={{ color: textColor }}>{`${value} answered (${data.completion?.toFixed(1)}% complete)`}</span>,
                            <span style={{ color: textColor }}>{`${name} - ${data.accuracy?.toFixed(1)}% accuracy`}</span>
                          ];
                        }
                        return [<span style={{ color: textColor }}>{`${value} questions`}</span>, <span style={{ color: textColor }}>{name}</span>];
                      }}
                      contentStyle={{ 
                        backgroundColor: isDarkMode ? '#0f2132' : '#fff',
                        background: isDarkMode ? '#0f2132' : '#fff',
                        color: isDarkMode ? '#ffffff' : '#333',
                        border: isDarkMode ? '1px solid #2c5282' : '1px solid #ddd',
                        borderRadius: '8px',
                        boxShadow: isDarkMode ? '0 4px 12px rgba(0, 0, 0, 0.25)' : '0 4px 12px rgba(0, 0, 0, 0.15)',
                        fontSize: windowWidth <= 480 ? '12px' : '14px'
                      }}
                      wrapperStyle={{
                        backgroundColor: isDarkMode ? '#0f2132' : '#fff',
                        background: isDarkMode ? '#0f2132' : '#fff',
                        color: isDarkMode ? '#ffffff' : '#333',
                        border: isDarkMode ? '1px solid #2c5282' : '1px solid #ddd',
                        borderRadius: '8px',
                        zIndex: 9999
                      }}
                    />
                    {windowWidth > 768 && (
                      <Legend 
                        layout="horizontal" 
                        verticalAlign="bottom" 
                        align="center" 
                        wrapperStyle={{
                          fontSize: '14px',
                          paddingTop: '0'
                        }}
                        formatter={(value) => (
                          <span style={{ color: isDarkMode ? 'white' : '#2c3e50' }}>{value}</span>
                        )}
                      />
                    )}
                  </PieChart>
                </ResponsiveContainer>
                
                {/* Mobile legend and selected segment details */}
                {windowWidth <= 768 && (
                  <div style={{ marginTop: '15px' }}>
                    {/* Selected segment details */}
                    {selectedPieSegment && (
                      <div style={{
                        background: isDarkMode ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.05)',
                        borderRadius: '8px',
                        padding: '12px',
                        marginBottom: '10px',
                        border: `2px solid ${COLORS[selectedPieSegment.index % COLORS.length]}`
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                          <div style={{
                            width: '12px',
                            height: '12px',
                            backgroundColor: COLORS[selectedPieSegment.index % COLORS.length],
                            borderRadius: '50%'
                          }}></div>
                          <h4 style={{ 
                            color: themeStyles.text.primary, 
                            margin: '0', 
                            fontSize: '14px',
                            fontWeight: '600'
                          }}>
                            {selectedPieSegment.name}
                          </h4>
                        </div>
                        <div style={{ 
                          color: themeStyles.text.secondary, 
                          fontSize: '12px',
                          lineHeight: '1.4'
                        }}>
                          <div>{selectedPieSegment.value} questions answered</div>
                          <div>{selectedPieSegment.completion?.toFixed(1)}% completion rate</div>
                          <div>{selectedPieSegment.accuracy?.toFixed(1)}% accuracy</div>
                        </div>
                      </div>
                    )}
                    
                    {/* Mobile legend */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: windowWidth <= 480 ? '1fr 1fr' : '1fr 1fr 1fr',
                      gap: '8px',
                      fontSize: '11px'
                    }}>
                      {pieChartData.map((entry, index) => (
                        <div 
                          key={`legend-${index}`}
                          onClick={() => handlePieClick(entry, index)}
                          className="mobile-legend-item"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '6px 8px',
                            borderRadius: '6px',
                            background: selectedPieSegment?.index === index 
                              ? (isDarkMode ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.1)')
                              : 'transparent',
                            cursor: 'pointer',
                            transition: 'background 0.2s ease'
                          }}
                        >
                          <div style={{
                            width: '10px',
                            height: '10px',
                            backgroundColor: COLORS[index % COLORS.length],
                            borderRadius: '50%',
                            flexShrink: 0
                          }}></div>
                          <span style={{ 
                            color: themeStyles.text.primary,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}>
                            {entry.name}
                          </span>
                        </div>
                      ))}
                    </div>
                    
                    {/* Tap instruction */}
                    {!selectedPieSegment && (
                      <div style={{
                        textAlign: 'center',
                        color: themeStyles.text.secondary,
                        fontSize: '12px',
                        marginTop: '10px',
                        fontStyle: 'italic'
                      }}>
                        Tap on a segment or color to see details
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="chart-box" style={{ 
            background: themeStyles.chartBox.background, 
            boxShadow: themeStyles.chartBox.shadow 
          }}>            <div className="chart-header">
              <h3 style={{ color: themeStyles.text.primary }}>Daily Performance (Last 7 Days)</h3>
              <FontAwesomeIcon 
                icon={faInfoCircle} 
                className="info-icon" 
                title="Daily questions answered and completion percentage" 
                style={{ color: themeStyles.text.secondary }}
              />
            </div>
            {chartLoading ? (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '250px',
                color: themeStyles.text.secondary 
              }}>
                Loading chart data...
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={dailyChartData}>
                  <CartesianGrid stroke={themeStyles.gridLines} vertical={false} />
                  <XAxis 
                    dataKey="day" 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fill: isDarkMode ? '#ffffff' : '#2c3e50' }}
                  />
                  <YAxis 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fill: isDarkMode ? '#ffffff' : '#2c3e50' }}
                  />
                  <RechartTooltip 
                    cursor={
                      isDarkMode
                        ? { fill: 'rgba(0,0,0,0.25)', stroke: 'rgba(255,255,255,0.2)' }
                        : { fill: 'rgba(0,0,0,0.06)', stroke: 'rgba(0,0,0,0.2)' }
                    }
                    labelStyle={{ color: isDarkMode ? '#ffffff' : '#333' }}
                    itemStyle={{ color: isDarkMode ? '#ffffff' : '#333' }}
                    formatter={(value, name) => {
                      const textColor = isDarkMode ? '#ffffff' : '#333'; // Define text color based on theme
                      if (name === 'questionsAnswered') return [<span style={{ color: textColor }}>{`${value} questions answered`}</span>, <span style={{ color: textColor }}>{'Questions Answered'}</span>];
                      if (name === 'totalQuestions') return [<span style={{ color: textColor }}>{`${value} total questions`}</span>, <span style={{ color: textColor }}>{'Total Questions'}</span>];
                      if (name === 'completionPercentage') return [<span style={{ color: textColor }}>{`${value}%`}</span>, <span style={{ color: textColor }}>{'Completion Rate'}</span>];
                      return [<span style={{ color: textColor }}>{value}</span>, <span style={{ color: textColor }}>{name}</span>];
                    }}
                    contentStyle={{ 
                      backgroundColor: isDarkMode ? '#0f2132' : '#fff',
                      background: isDarkMode ? '#0f2132' : '#fff',
                      color: isDarkMode ? '#ffffff' : '#333',
                      border: isDarkMode ? '1px solid #2c5282' : '1px solid #ddd',
                      borderRadius: '8px',
                      boxShadow: isDarkMode ? '0 4px 12px rgba(0, 0, 0, 0.25)' : '0 4px 12px rgba(0, 0, 0, 0.15)'
                    }}
                    wrapperStyle={{
                      backgroundColor: isDarkMode ? '#0f2132' : '#fff',
                      background: isDarkMode ? '#0f2132' : '#fff',
                      color: isDarkMode ? '#ffffff' : '#333',
                      border: isDarkMode ? '1px solid #2c5282' : '1px solid #ddd',
                      borderRadius: '8px',
                      zIndex: 9999
                    }}
                  />
                  <Bar 
                    dataKey="questionsAnswered" 
                    fill={isDarkMode ? "#6abfef" : "#6baee8"} 
                    radius={[3, 3, 0, 0]}
                    name="Questions Answered"
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Second row of charts */}
        <div className="charts-row">
          <div className="chart-box" style={{ 
            background: themeStyles.chartBox.background, 
            boxShadow: themeStyles.chartBox.shadow 
          }}>            <div className="chart-header">
              <h3 style={{ color: themeStyles.text.primary }}>Study Time (Last 7 Days)</h3>
              <FontAwesomeIcon 
                icon={faInfoCircle} 
                className="info-icon" 
                title="Hours spent on practice tests and exams based on your actual session data" 
                style={{ color: themeStyles.text.secondary }}
              />
            </div><ResponsiveContainer width="100%" height={200}>
              {isLoading ? (
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  height: '100%',
                  color: themeStyles.text.secondary 
                }}>
                  Loading study time data...
                </div>
              ) : (
                <AreaChart data={studyTimeData}>
                  <defs>
                    <linearGradient id="colorHours" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={isDarkMode ? "#8feead" : "#82ca9d"} stopOpacity={0.8}/>
                      <stop offset="95%" stopColor={isDarkMode ? "#8feead" : "#82ca9d"} stopOpacity={0.2}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke={themeStyles.gridLines} vertical={false} />
                  <XAxis 
                    dataKey="day" 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fill: isDarkMode ? '#ffffff' : '#2c3e50' }} // Improved label color for light mode
                  />
                  <YAxis 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fill: isDarkMode ? '#ffffff' : '#2c3e50' }} // Improved label color for light mode
                  />
                  <RechartTooltip 
                    cursor={
                      isDarkMode
                        ? { fill: 'rgba(0,0,0,0.25)', stroke: 'rgba(255,255,255,0.2)' }
                        : { fill: 'rgba(0,0,0,0.06)', stroke: 'rgba(0,0,0,0.2)' }
                    }
                    labelStyle={{ color: isDarkMode ? '#ffffff' : '#333' }}
                    itemStyle={{ color: isDarkMode ? '#ffffff' : '#333' }}
                    contentStyle={{ 
                      backgroundColor: isDarkMode ? '#0f2132' : '#fff',
                      background: isDarkMode ? '#0f2132' : '#fff',
                      border: isDarkMode ? '1px solid #2c5282' : '1px solid #ddd',
                      color: isDarkMode ? '#ffffff' : '#333',
                      borderRadius: '8px',
                      boxShadow: isDarkMode ? '0 4px 12px rgba(0, 0, 0, 0.25)' : '0 4px 12px rgba(0, 0, 0, 0.15)'
                    }}
                    wrapperStyle={{
                      backgroundColor: isDarkMode ? '#0f2132' : '#fff',
                      background: isDarkMode ? '#0f2132' : '#fff',
                      color: isDarkMode ? '#ffffff' : '#333',
                      border: isDarkMode ? '1px solid #2c5282' : '1px solid #ddd',
                      borderRadius: '8px',
                      zIndex: 9999
                    }}
                    formatter={(value, name) => {
                      const textColor = isDarkMode ? '#ffffff' : '#333';
                      return [<span style={{ color: textColor }}>{`${value}h`}</span>, <span style={{ color: textColor }}>{'Study Time'}</span>];
                    }}
                    labelFormatter={(label) => `${label}`}
                  />
                  <Area type="monotone" dataKey="hours" stroke={isDarkMode ? "#8feead" : "#82ca9d"} fillOpacity={1} fill="url(#colorHours)" />
                </AreaChart>
              )}
            </ResponsiveContainer>
          </div>
            <div className="chart-box recent-tests" style={{ 
            background: themeStyles.chartBox.background, 
            boxShadow: themeStyles.chartBox.shadow 
          }}>
            <div className="chart-header">
              <h3 style={{ color: themeStyles.text.primary }}>Accuracy Report by Category</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <button
                  className="session-action-clean-button"
                  onClick={toggleAccuracySortOrder}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '6px',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease',
                    color: themeStyles.text.secondary,
                    boxShadow: 'none',
                    width: 'auto',
                    height: 'auto',
                    transform: 'none'
                  }}
                  title={`Sort ${accuracySortOrder === 'high-to-low' ? 'low to high' : 'high to low'}`}
                  onMouseEnter={(e) => {
                    if (!isDarkMode) {
                      e.target.style.color = '#333333';
                      e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
                    } else {
                      e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                      e.target.style.color = '#ffffff';
                    }
                    e.target.style.border = 'none';
                    e.target.style.boxShadow = 'none';
                    e.target.style.transform = 'none';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = themeStyles.text.secondary;
                    e.target.style.border = 'none';
                    e.target.style.boxShadow = 'none';
                    e.target.style.transform = 'none';
                  }}
                >
                  <FontAwesomeIcon 
                    icon={accuracySortOrder === 'high-to-low' ? faSortAmountDown : faSortAmountUp}
                    style={{ fontSize: '16px' }}
                  />
                </button>
                <FontAwesomeIcon 
                  icon={faInfoCircle} 
                  className="info-icon" 
                  title="Accuracy percentage by category based on your test performance" 
                  style={{ color: themeStyles.text.secondary }}
                />
              </div>
            </div>
            <div className="recent-tests-list">
              {chartLoading ? (
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  padding: '20px',
                  color: themeStyles.text.secondary 
                }}>
                  Loading accuracy data...
                </div>
              ) : accuracyReportData.length > 0 ? (
                accuracyReportData.map((category, index) => (
                  <div key={`${category.categoryName}-${index}`} className="recent-test-item" style={{ background: themeStyles.testItem.background }}>
                    <div className="test-info">
                      <h4 style={{ color: themeStyles.text.primary }}>{category.categoryName}</h4>
                      <span className="test-date" style={{ color: themeStyles.text.secondary }}>
                        {category.questionsAnswered} of {category.totalQuestions} questions answered
                      </span>
                    </div>
                    <div className="test-score">
                      <div className={`score-badge ${
                        category.accuracyPercentage >= 80 ? 'excellent' : 
                        category.accuracyPercentage >= 70 ? 'good' : 'needs-improvement'
                      }`}>
                        {category.accuracyPercentage}%
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  padding: '20px',
                  color: themeStyles.text.secondary,
                  flexDirection: 'column',
                  gap: '10px'
                }}>
                  <FontAwesomeIcon icon={faChartLine} style={{ fontSize: '24px', opacity: 0.5 }} />
                  <span>No category data available</span>
                  <span style={{ fontSize: '12px', opacity: 0.7 }}>Take some tests to see your accuracy by category</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Add new third row for our new charts */}
        <div className="charts-row">
          {/* Strong vs Weak Category Chart */}          <div className="chart-box" style={{ 
            background: themeStyles.chartBox.background, 
            boxShadow: themeStyles.chartBox.shadow 
          }}>
            <div className="chart-header">
              <h3 style={{ color: themeStyles.text.primary }}>Category Accuracy</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                {accuracyChartData.length > 5 && (
                  <button
                    onClick={() => setShowAllCategories(!showAllCategories)}
                    style={{
                      background: isDarkMode ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.1)',
                      color: themeStyles.text.primary,
                      border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'}`,
                      borderRadius: '4px',
                      padding: '4px 8px',
                      fontSize: '12px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = isDarkMode ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = isDarkMode ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.1)';
                    }}
                  >
                    {showAllCategories ? `Show Top 5` : `Show All (${accuracyChartData.length})`}
                  </button>
                )}
                <FontAwesomeIcon 
                  icon={faInfoCircle} 
                  className="info-icon" 
                  title="Accuracy percentage by category" 
                  style={{ color: themeStyles.text.secondary }}
                />
              </div>
            </div>
            {chartLoading ? (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '250px',
                color: themeStyles.text.secondary 
              }}>
                Loading chart data...
              </div>            ) : (
              <div style={{ 
                height: showAllCategories ? '500px' : '350px',
                overflowY: showAllCategories ? 'auto' : 'visible',
                transition: 'height 0.3s ease',
                border: showAllCategories ? `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}` : 'none',
                borderRadius: showAllCategories ? '4px' : '0',
                padding: showAllCategories ? '10px' : '0'
              }}>
                <div style={{ 
                  height: showAllCategories ? `${Math.max(350, displayedAccuracyData.length * 40)}px` : '350px',
                  minHeight: '350px'
                }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={displayedAccuracyData}
                      layout="vertical"
                      margin={{ 
                        top: 10, 
                        right: 30, 
                        left: windowWidth <= 768 ? 80 : 130, 
                        bottom: 10 
                      }}
                      barCategoryGap="10%"
                    >
                      <CartesianGrid horizontal={true} vertical={false} stroke={themeStyles.gridLines} />
                      <XAxis 
                        type="number"
                        axisLine={false} 
                        tickLine={false} 
                        tick={{ fill: isDarkMode ? '#ffffff' : '#2c3e50', fontSize: windowWidth <= 768 ? 10 : 12 }}
                        domain={[0, 'dataMax']}
                      />
                      <YAxis 
                        dataKey="category" 
                        type="category"
                        axisLine={false} 
                        tickLine={false} 
                        tick={{ 
                          fill: isDarkMode ? '#ffffff' : '#2c3e50', 
                          fontSize: windowWidth <= 768 ? 9 : 10,
                          textAnchor: 'end'
                        }}
                        width={windowWidth <= 768 ? 70 : 125}
                        interval={0}
                        tickFormatter={(value) => {
                          // Truncate long category names - more aggressive on mobile
                          const maxLength = windowWidth <= 768 ? 12 : 16;
                          const words = value.split(' ');
                          
                          if (windowWidth <= 768 && words.length > 1) {
                            return words[0] + '...';
                          } else if (words.length > 2) {
                            return words.slice(0, 2).join(' ') + '...';
                          }
                          
                          // If it's still too long, truncate and add ellipsis
                          if (value.length > maxLength) {
                            return value.substring(0, maxLength - 2) + '...';
                          }
                          return value;
                        }}
                      />
                      <RechartTooltip
                        cursor={
                          isDarkMode
                            ? { fill: 'rgba(0,0,0,0.25)', stroke: 'rgba(255,255,255,0.2)' }
                            : { fill: 'rgba(0,0,0,0.06)', stroke: 'rgba(0,0,0,0.2)' }
                        }
                        labelStyle={{ color: isDarkMode ? '#ffffff' : '#333' }}
                        itemStyle={{ color: isDarkMode ? '#ffffff' : '#333' }}
                        formatter={(value, name) => {
                          const textColor = isDarkMode ? '#ffffff' : '#333'; // Define text color based on theme
                          if (name === 'correct') return [<span style={{ color: textColor }}>{`${value} correct answers`}</span>, <span style={{ color: textColor }}>{'Correct Answers'}</span>];
                          if (name === 'total') return [<span style={{ color: textColor }}>{`${value} total answered`}</span>, <span style={{ color: textColor }}>{'Total Answered'}</span>];
                          return [<span style={{ color: textColor }}>{value}</span>, <span style={{ color: textColor }}>{name}</span>];
                        }}
                        labelFormatter={(value) => {
                          const textColor = isDarkMode ? '#ffffff' : '#333'; // Define text color based on theme
                          // Show full category name in tooltip
                          const fullCategory = displayedAccuracyData.find(item =>
                            item.category === value ||
                            item.category.startsWith(value.replace('...', ''))
                          )?.category || value;
                          return <span style={{ color: textColor }}>{`Category: ${fullCategory}`}</span>;
                        }}
                        contentStyle={{
                          backgroundColor: isDarkMode ? '#0f2132' : '#fff',
                          background: isDarkMode ? '#0f2132' : '#fff',
                          border: isDarkMode ? '1px solid #2c5282' : '1px solid #ddd',
                          color: isDarkMode ? '#ffffff' : '#333',
                          borderRadius: '8px',
                          boxShadow: isDarkMode ? '0 4px 12px rgba(0, 0, 0, 0.25)' : '0 4px 12px rgba(0, 0, 0, 0.15)'
                        }}
                        wrapperStyle={{
                          backgroundColor: isDarkMode ? '#0f2132' : '#fff',
                          background: isDarkMode ? '#0f2132' : '#fff',
                          color: isDarkMode ? '#ffffff' : '#333',
                          border: isDarkMode ? '1px solid #2c5282' : '1px solid #ddd',
                          borderRadius: '8px',
                          zIndex: 9999
                        }}
                      />
                      <Bar dataKey="total" name="Total Answered" fill={isDarkMode ? "#7986CB" : "#9FA8DA"} radius={[0, 3, 3, 0]} />
                      <Bar dataKey="correct" name="Correct" fill={isDarkMode ? "#4DB6AC" : "#80CBC4"} radius={[0, 3, 3, 0]} />
                      <Legend
                        formatter={(value) => (
                          <span style={{ color: isDarkMode ? 'white' : '#2c3e50' }}>{value}</span>
                        )}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>

          {/* Total Questions Progress Chart */}          <div className="chart-box" style={{ 
            background: themeStyles.chartBox.background, 
            boxShadow: themeStyles.chartBox.shadow 
          }}>
            <div className="chart-header">
              <h3 style={{ color: themeStyles.text.primary }}>Overall Progress</h3>
              <FontAwesomeIcon 
                icon={faInfoCircle} 
                className="info-icon" 
                title="Overall question progress and accuracy" 
                style={{ color: themeStyles.text.secondary }}
              />
            </div>
            {chartLoading ? (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '250px',
                color: themeStyles.text.secondary 
              }}>
                Loading chart data...
              </div>
            ) : (
              <div style={{ padding: '24px 0 0 0', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                {/* Summary Stats */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: '32px',
                  marginBottom: '16px',
                  width: '100%',
                  maxWidth: '500px'
                }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 22, fontWeight: 700, color: '#8884d8' }}>
                      {overallProgressData[0]?.value ?? 0}
                    </div>
                    <div style={{ fontSize: 13, color: themeStyles.text.secondary }}>
                      Total Questions
                    </div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 22, fontWeight: 700, color: '#82ca9d' }}>
                      {overallProgressData[1]?.value ?? 0}
                      <span style={{ fontSize: 13, color: themeStyles.text.secondary, marginLeft: 4 }}>
                        ({overallProgressData[0]?.value ? Math.round((overallProgressData[1]?.value / overallProgressData[0]?.value) * 100) : 0}%)
                      </span>
                    </div>
                    <div style={{ fontSize: 13, color: themeStyles.text.secondary }}>
                      Answered
                    </div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 22, fontWeight: 700, color: '#0088FE' }}>
                      {overallProgressData[2]?.value ?? 0}
                      <span style={{ fontSize: 13, color: themeStyles.text.secondary, marginLeft: 4 }}>
                        ({overallProgressData[1]?.value ? Math.round((overallProgressData[2]?.value / overallProgressData[1]?.value) * 100) : 0}% accuracy)
                      </span>
                    </div>
                    <div style={{ fontSize: 13, color: themeStyles.text.secondary }}>
                      Correct
                    </div>
                  </div>
                </div>
                {/* Horizontal Bar Chart */}
                <ResponsiveContainer width="90%" height={120}>
                  <BarChart
                    data={[
                      { name: 'Answered', value: overallProgressData[1]?.value ?? 0, fill: '#82ca9d' },
                      { name: 'Correct', value: overallProgressData[2]?.value ?? 0, fill: '#0088FE' }
                    ]}
                    layout="vertical"
                    margin={{ top: 10, right: 30, left: 60, bottom: 10 }}
                  >
                    <CartesianGrid horizontal={false} vertical={true} stroke={themeStyles.gridLines} />
                    <XAxis
                      type="number"
                      domain={[0, overallProgressData[0]?.value ?? 1]}
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: isDarkMode ? '#ffffff' : '#2c3e50', fontSize: 12 }}
                    />
                    <YAxis
                      type="category"
                      dataKey="name"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: isDarkMode ? '#ffffff' : '#2c3e50', fontSize: 14 }}
                    />
                    <RechartTooltip
                      cursor={
                        isDarkMode
                          ? { fill: 'rgba(0,0,0,0.25)', stroke: 'rgba(255,255,255,0.2)' }
                          : { fill: 'rgba(0,0,0,0.06)', stroke: 'rgba(0,0,0,0.2)' }
                      }
                      labelStyle={{ color: isDarkMode ? '#ffffff' : '#333' }}
                      itemStyle={{ color: isDarkMode ? '#ffffff' : '#333' }}
                      formatter={(value, name) => {
                        const textColor = isDarkMode ? '#ffffff' : '#333';
                        if (name === 'Answered') {
                          const total = overallProgressData[0]?.value ?? 1;
                          return [
                            <span style={{ color: textColor }}>{value}</span>,
                            <span style={{ color: textColor }}>{`${name} (${Math.round((value/total)*100)}%)`}</span>
                          ];
                        }
                        if (name === 'Correct') {
                          const answered = overallProgressData[1]?.value ?? 1;
                          return [
                            <span style={{ color: textColor }}>{value}</span>,
                            <span style={{ color: textColor }}>{`${name} (${Math.round((value/answered)*100)}% accuracy)`}</span>
                          ];
                        }
                        return [<span style={{ color: textColor }}>{value}</span>, <span style={{ color: textColor }}>{name}</span>];
                      }}
                      contentStyle={{
                        backgroundColor: isDarkMode ? '#0f2132' : '#fff',
                        background: isDarkMode ? '#0f2132' : '#fff',
                        color: isDarkMode ? '#ffffff' : '#333',
                        border: isDarkMode ? '1px solid #2c5282' : '1px solid #ddd',
                        borderRadius: '8px',
                        boxShadow: isDarkMode ? '0 4px 12px rgba(0, 0, 0, 0.25)' : '0 4px 12px rgba(0, 0, 0, 0.15)'
                      }}
                      wrapperStyle={{
                        backgroundColor: isDarkMode ? '#0f2132' : '#fff',
                        background: isDarkMode ? '#0f2132' : '#fff',
                        color: isDarkMode ? '#ffffff' : '#333',
                        border: isDarkMode ? '1px solid #2c5282' : '1px solid #ddd',
                        borderRadius: '8px',
                        zIndex: 9999
                      }}
                    />
                    <Bar dataKey="value" fill={({ payload }) => payload.fill} radius={[0, 8, 8, 0]} barSize={28} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>

        {/* Fourth row - Additional useful charts */}
        <div className="charts-row">
          {/* Weekly Completion Trend Chart */}
          <div className="chart-box" style={{ 
            background: themeStyles.chartBox.background, 
            boxShadow: themeStyles.chartBox.shadow 
          }}>
            <div className="chart-header">
              <h3 style={{ color: themeStyles.text.primary }}>Weekly Progress Trend</h3>
              <FontAwesomeIcon 
                icon={faInfoCircle} 
                className="info-icon" 
                title="Completion percentage trend over the last 7 days" 
                style={{ color: themeStyles.text.secondary }}
              />
            </div>
            {chartLoading ? (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '200px',
                color: themeStyles.text.secondary 
              }}>
                Loading chart data...
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={dailyChartData}>
                  <defs>
                    <linearGradient id="colorCompletion" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={isDarkMode ? "#4DB6AC" : "#4CAF50"} stopOpacity={0.8}/>
                      <stop offset="95%" stopColor={isDarkMode ? "#4DB6AC" : "#4CAF50"} stopOpacity={0.2}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke={themeStyles.gridLines} vertical={false} />
                  <XAxis 
                    dataKey="day" 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fill: isDarkMode ? '#ffffff' : '#2c3e50' }}
                  />
                  <YAxis 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fill: isDarkMode ? '#ffffff' : '#2c3e50' }}
                    domain={[0, 100]}
                  />
                  <RechartTooltip 
                    cursor={
                      isDarkMode
                        ? { fill: 'rgba(0,0,0,0.25)', stroke: 'rgba(255,255,255,0.2)' }
                        : { fill: 'rgba(0,0,0,0.06)', stroke: 'rgba(0,0,0,0.2)' }
                    }
                    labelStyle={{ color: isDarkMode ? '#ffffff' : '#333' }}
                    itemStyle={{ color: isDarkMode ? '#ffffff' : '#333' }}
                    formatter={(value, name) => {
                      const textColor = isDarkMode ? '#ffffff' : '#333'; // Define text color based on theme
                      return [<span style={{ color: textColor }}>{`${value}%`}</span>, <span style={{ color: textColor }}>{'Completion Rate'}</span>];
                    }}
                    contentStyle={{ 
                      backgroundColor: isDarkMode ? '#0f2132' : '#fff',
                      background: isDarkMode ? '#0f2132' : '#fff',
                      color: isDarkMode ? '#ffffff' : '#333',
                      border: isDarkMode ? '1px solid #2c5282' : '1px solid #ddd',
                      borderRadius: '8px',
                      boxShadow: isDarkMode ? '0 4px 12px rgba(0, 0, 0, 0.25)' : '0 4px 12px rgba(0, 0, 0, 0.15)'
                    }}
                    wrapperStyle={{
                      backgroundColor: isDarkMode ? '#0f2132' : '#fff',
                      background: isDarkMode ? '#0f2132' : '#fff',
                      color: isDarkMode ? '#ffffff' : '#333',
                      border: isDarkMode ? '1px solid #2c5282' : '1px solid #ddd',
                      borderRadius: '8px',
                      zIndex: 9999
                    }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="completionPercentage" 
                    stroke={isDarkMode ? "#4DB6AC" : "#4CAF50"} 
                    fillOpacity={1} 
                    fill="url(#colorCompletion)" 
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Category Performance Summary */}
          <div className="chart-box" style={{ 
            background: themeStyles.chartBox.background, 
            boxShadow: themeStyles.chartBox.shadow 
          }}>
            <div className="chart-header">
              <h3 style={{ color: themeStyles.text.primary }}>Performance Summary</h3>
              <FontAwesomeIcon 
                icon={faInfoCircle} 
                className="info-icon" 
                title="Overall performance metrics" 
                style={{ color: themeStyles.text.secondary }}
              />
            </div>
            {chartLoading ? (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '200px',
                color: themeStyles.text.secondary 
              }}>
                Loading chart data...
              </div>
            ) : (              <div style={{ padding: '20px 0' }}>
                {rawCategoryReport.length > 0 ? (
                  <div>
                    {/* Overall Stats */}
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(2, 1fr)', 
                      gap: '15px', 
                      marginBottom: '20px' 
                    }}>
                      <div style={{ 
                        textAlign: 'center', 
                        padding: '15px', 
                        borderRadius: '8px', 
                        background: isDarkMode ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.05)' 
                      }}>
                        <div style={{ 
                          fontSize: '24px', 
                          fontWeight: 'bold', 
                          color: isDarkMode ? '#4DB6AC' : '#4CAF50' 
                        }}>
                          {rawCategoryReport.reduce((sum, cat) => sum + cat.questionsAnswered, 0)}
                        </div>
                        <div style={{ 
                          fontSize: '12px', 
                          color: themeStyles.text.secondary 
                        }}>
                          Total Questions Answered
                        </div>
                      </div>
                      <div style={{ 
                        textAlign: 'center', 
                        padding: '15px', 
                        borderRadius: '8px', 
                        background: isDarkMode ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.05)' 
                      }}>
                        <div style={{ 
                          fontSize: '24px', 
                          fontWeight: 'bold', 
                          color: isDarkMode ? '#FFA726' : '#FF9800' 
                        }}>
                          {Math.round(rawCategoryReport.reduce((sum, cat) => sum + (cat.accuracyPercentage || 0), 0) / rawCategoryReport.length)}%
                        </div>
                        <div style={{ 
                          fontSize: '12px', 
                          color: themeStyles.text.secondary 
                        }}>
                          Average Accuracy
                        </div>
                      </div>
                    </div>
                    
                    {/* Top Performing Category */}
                    {(() => {
                      const topCategory = rawCategoryReport.reduce((max, cat) => 
                        (cat.accuracyPercentage > max.accuracyPercentage) ? cat : max, rawCategoryReport[0]);
                      return (
                        <div style={{ 
                          textAlign: 'center', 
                          padding: '15px', 
                          borderRadius: '8px', 
                          background: isDarkMode ? 'rgba(76, 175, 80, 0.1)' : 'rgba(76, 175, 80, 0.1)',
                          border: `1px solid ${isDarkMode ? '#4CAF50' : '#81C784'}`
                        }}>
                          <div style={{ 
                            fontSize: '14px', 
                            fontWeight: 'bold', 
                            color: themeStyles.text.primary,
                            marginBottom: '5px'
                          }}>
                            🏆 Best Performing Category
                          </div>
                          <div style={{ 
                            fontSize: '16px', 
                            color: isDarkMode ? '#4CAF50' : '#2E7D32' 
                          }}>
                            {topCategory.categoryName} - {topCategory.accuracyPercentage}% accuracy
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                ) : (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '40px',
                    color: themeStyles.text.secondary 
                  }}>
                    No performance data available. Start practicing to see your stats!
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
          </>
        )}
      </div>      <div className="left-action">
        <button 
          className="action-button report-button" 
          style={{ 
            backgroundColor: themeStyles.actionButton.background,
            color: themeStyles.actionButton.color
          }}
          onClick={() => navigate('/performance-history')}
        >
          <FontAwesomeIcon icon={faChartLine} /> View Full Performance Report
        </button>
      </div>

      {/* Confirmation Dialog */}
      {confirmDialog.open && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: themeStyles.modalOverlay.background,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: themeStyles.modal.background,
            borderRadius: '8px',
            padding: '20px',
            width: '400px',
            maxWidth: '90%',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            border: themeStyles.modal.border
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h3 style={{ color: themeStyles.text.primary, margin: 0 }}>
                {confirmDialog.title}
              </h3>
              <button
                onClick={handleCloseDialog}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: themeStyles.text.secondary,
                  fontSize: '1.2rem'
                }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            
            <p style={{ color: themeStyles.text.secondary, marginBottom: '20px' }}>
              {confirmDialog.message}
            </p>
            
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                onClick={handleCloseDialog}
                style={{
                  background: themeStyles.button.cancel,
                  color: themeStyles.button.cancelText,
                  border: 'none',
                  borderRadius: '4px',
                  padding: '8px 15px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDialog.onConfirm}
                style={{
                  background: confirmDialog.type === 'delete' ? themeStyles.button.confirm : '#2196F3',
                  color: themeStyles.button.confirmText,
                  border: 'none',
                  borderRadius: '4px',
                  padding: '8px 15px',
                  cursor: 'pointer'
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      <Footer />
    </div>
  );
}

export default Dashboard;
